/**
 * Doküman kütüphanesi ekranlarının metinleri (M8a3; ADR 0027, 0028, 0032). Ekran adları
 * tasarımdaki gibi İngilizce (`gemi-ism-formlari.jpeg`); Türkçe ikinci dil. Basılı kopya altlığı
 * İngilizcedir (ADR 0028 #30 A): Türkçe metinde de aynı kalır.
 */
import { documentsLibraryEn, documentsLibraryTr } from './documents-library.js';
import { documentsOfficeEn, documentsOfficeTr } from './documents-office.js';
import { documentsTreeRecordsEn, documentsTreeRecordsTr } from './documents-tree-records.js';

const printFooter = {
  uncontrolled:
    'UNCONTROLLED WHEN PRINTED unless stamped CONTROLLED COPY – verify the current revision in Felixmate before use',
  reference: 'NOT FOR OPERATIONAL USE – reference copy; current: {{current}}',
  none: 'none (withdrawn {{date}})',
  contentOf: 'content of {{revision}}',
  effective: 'effective {{date}}',
  printed: 'printed {{at}} by {{by}}',
  hash: 'rev-hash {{hash}}',
};

export const documentsEn = {
  sections: {
    ism: {
      title: 'ISM Forms',
      tree: 'Form Tree',
      details: 'Form Details',
      information: 'Form Information',
      search: 'Search form...',
      view: 'Form View',
      number: 'Form No',
      name: 'Form Name',
      open: 'Open Form',
      download: 'Download Form',
      print: 'Print Form',
      empty: 'No forms in the library yet.',
    },
    manuals: {
      title: 'Manuals',
      tree: 'Manual Tree',
      details: 'Manual Details',
      information: 'Manual Information',
      search: 'Search manual...',
      view: 'Document View',
      number: 'Document No',
      name: 'Document Name',
      open: 'Open Document',
      download: 'Download Document',
      print: 'Print Document',
      empty: 'No manuals in the library yet.',
    },
    pms_instructions: {
      title: 'PMS Instructions',
      tree: 'Instruction Tree',
      details: 'Instruction Details',
      information: 'Instruction Information',
      search: 'Search instruction...',
      view: 'Instruction View',
      number: 'Instruction No',
      name: 'Instruction Name',
      open: 'Open Instruction',
      download: 'Download Instruction',
      print: 'Print Instruction',
      empty: 'No PMS instructions in the library yet.',
    },
  },
  /** "Remove document" (ADR 0142 §5): yanlış yüklenen, kullanılmamış doküman. */
  remove: {
    title: 'Remove {{title}}',
    text: 'Use this for a document uploaded by mistake. It leaves the library; its revisions and history are kept. A document in force must be withdrawn first; a document that was used (form records, acknowledgements, read confirmations) cannot be removed and is corrected with a new revision.',
    cancel: 'Cancel',
    confirm: 'Remove document',
    done: 'The document was removed.',
  },
  head: {
    // Revizyonun girişi adıyla bulunur (ortağın geri bildirimi 2026-10-18; ADR 0081)
    edit: 'New revision',
    remove: 'Remove document',
    download: 'Download',
    print: 'Print',
    distribution: 'Distribution',
    newDocument: 'New Document',
    newDocumentFormHint:
      'New Document adds a new form to the library. To fill in a form, select it and choose Fill in.',
    editBlocked: '{{revision}} is still open. Finish or discard it before starting a new revision.',
    revisionHint:
      'The Office Manager or the Company Administrator prepares a new revision; you approve it and publish it to the vessels.',
  },
  fields: {
    category: 'Category',
    fileType: 'File Type',
    version: 'Version',
    updated: 'Updated',
    lastUpdated: 'Last Updated',
    createdBy: 'Created By',
    description: 'Description',
    noDescription: 'No description.',
  },
  tabs: {
    information: 'Information',
    history: 'History',
    attachments: 'Attachments',
  },
  actions: {
    openDocument: 'Open Document',
    title: 'Actions',
    openHint: 'Your role cannot open a record of this form.',
    officeRecords:
      'Records of this form are entered on board in the ship application: open the form there, fill it in, upload the file and submit. Submitted records and approvals appear in Form History.',
    officeHint: 'Records are entered on board, not in the office.',
    formHistory: 'Open Form History',
    favoriteAdd: 'Add to Favorites',
    favoriteRemove: 'Remove from Favorites',
  },
  related: {
    title: 'Related Documents',
    none: 'No related documents.',
    download: 'Download {{name}}',
    notAvailable: 'No current revision',
  },
  fileKinds: {
    docx: 'Word',
    xlsx: 'Excel',
    pdf: 'PDF',
    jpg: 'Image',
    png: 'Image',
  },
  fileKindsLong: {
    docx: 'Word Document',
    xlsx: 'Excel Workbook',
    pdf: 'PDF Document',
    jpg: 'JPEG Image',
    png: 'PNG Image',
  },
  states: {
    draft: 'Draft',
    pending_approval: 'Pending Approval',
    approved: 'Approved',
    scheduled: 'Scheduled',
    published: 'Published',
    superseded: 'Superseded',
    withdrawn: 'Withdrawn',
    cancelled: 'Cancelled',
    discarded: 'Discarded',
  },
  badges: {
    updated: 'Updated',
    withdrawn: 'Withdrawn',
    notOnBoard: 'Not on board',
    favorite: 'Favorite',
  },
  info: {
    number: 'Number',
    title: 'Title',
    kind: 'Type',
    kinds: { form: 'Form', procedure: 'Procedure', manual: 'Manual' },
    folder: 'Folder',
    scope: 'Distribution',
    scopes: { fleet: 'All vessels', vessels: 'Selected vessels', office: 'Office only' },
    branch: 'Branch',
    main: 'Main',
    currentRevision: 'Current revision',
    effective: 'Effective date',
    preparedBy: 'Prepared by',
    approvedBy: 'Approved by',
    changeSummary: 'Change summary',
    noCurrent: 'No current revision',
    scheduled: 'Rev. {{revision}} takes effect on {{date}}.',
  },
  history: {
    seq: '#',
    revision: 'Rev. No',
    status: 'Status',
    date: 'Date',
    uploadedBy: 'Uploaded by',
    approvedBy: 'Approved by',
    summary: 'Change summary',
    preview: 'Preview Rev. {{revision}}',
    previewing: 'Shown',
    empty: 'No revisions yet.',
    caption: 'Revisions of the main branch',
  },
  attachments: {
    file: 'File',
    role: 'Type',
    roles: { attachment: 'Attachment', print_rendition: 'Print PDF' },
    size: 'Size',
    empty: 'No attachments for this revision.',
  },
  tree: {
    empty: 'No documents in this section yet.',
    noMatch: 'No document matches “{{query}}”.',
    clear: 'Clear search',
    count_one: '{{count}} document',
    count_other: '{{count}} documents',
    // Gösterim grupları (ADR 0038 §3, §9)
    retiredGroup: 'Retired folders',
    otherGroup: 'Other company documents',
    company: 'Company',
    // Ağaç satırında açık revizyon ve klasör içi sıra (ortağın geri bildirimi 2026-10-17; ADR 0069)
    awaitingApproval: 'awaiting approval',
    openRevision: {
      draft: '{{revision}} draft',
      pending_approval: '{{revision}} awaiting approval',
      approved: '{{revision}} approved',
      scheduled: '{{revision}} scheduled',
    },
    sort: {
      label: 'Sort documents',
      order: 'Order',
      date: 'Date',
    },
    // Formun altındaki kayıtlar (ADR 0072)
    records: documentsTreeRecordsEn,
  },
  screen: {
    loading: 'Loading library…',
    error: 'The library could not be loaded.',
    retry: 'Try again',
    select: 'Select a document from the tree.',
    noRevision: 'This document has no revision yet.',
    detailError: 'The document could not be loaded.',
    showTree: 'Show tree',
    dismiss: 'Dismiss',
    // Ofiste boş kütüphanede yönlendirme (ADR 0081)
    officeEmpty:
      'Add a document with New Document. To revise one, select it and choose New revision; after the DPA approves it, Publish to vessels sends it to the vessels in its distribution.',
  },
  viewer: {
    toolbar: 'Preview',
    zoomOut: 'Zoom out',
    zoomIn: 'Zoom in',
    fit: 'Fit to width',
    zoom: 'Zoom',
    download: 'Download',
    print: 'Print',
    fullscreen: 'Full screen',
    exitFullscreen: 'Exit full screen',
    page: 'Page {{page}} of {{count}}',
    pageApprox: 'Page {{page}} of ≈{{count}}',
    approximate: 'Approximate preview: page breaks may differ from Word.',
    loading: 'Loading preview…',
    error: 'The file could not be previewed.',
    unsupported: 'This file type cannot be previewed.',
    tooLarge: 'The file is too large to preview here ({{size}}). Download it to open.',
    sheets: 'Sheets',
    notCurrent: 'This revision is not current – reference only. Download anyway?',
    notCurrentShip: 'Only the current revision can be downloaded or printed on board.',
    blocked: {
      noRevision: 'This document has no revision yet.',
      notCurrent: 'Only the current revision can be downloaded or printed on board.',
      notOnBoard: 'The file is not on board yet.',
    },
    stateNotice: 'This revision is not current ({{state}}): reference only.',
    preparingPrint: 'Preparing pages for printing…',
    find: 'Find in document',
    findNext: 'Next match',
    findPrevious: 'Previous match',
    findClose: 'Close search',
    matches: '{{index}} of {{count}}',
    noMatches: 'No matches',
    truncated:
      'Showing the first {{rows}} rows and {{columns}} columns of {{totalRows}} × {{totalColumns}}. Download the file to see all of it.',
  },
  printFooter,
  notOnBoard: {
    title: 'Not on board yet',
    description:
      'The file of this revision has not been downloaded to the ship server yet. It can be previewed once it is verified on board.',
    position: 'Queue position',
    size: 'Size',
    reason: 'Status',
    reasons: {
      pending: 'Waiting for download',
      downloading: 'Downloading',
      deferred_quota: 'Deferred: storage quota or disk space',
      failed: 'Download failed',
      window: 'Waiting for the bulk download window',
      paused: 'Paused: bulk downloads are paused on board',
      manual: 'Waiting for a manual download (Download now)',
      monthly_cap: 'Waiting: the monthly bulk download limit is reached',
      retry: 'Retrying after a connection problem',
      unknown: 'Not in the download queue',
    },
    downloadNow: 'Download now',
    requested: 'Download requested. It starts with the next connection.',
  },
  changes: {
    title: 'What changed in {{revision}}',
    acknowledge: 'Acknowledge',
    acknowledgeWithdrawal: 'Acknowledge withdrawal',
    acknowledged: 'Acknowledged by {{name}} on {{date}}',
    withdrawn: '{{revision}} was withdrawn. Remove printed copies.',
    upcoming: '{{revision}} takes effect on {{date}}. Replace printed copies then.',
  },
  acknowledge: {
    title: 'Acknowledge revision',
    titleWithdrawal: 'Acknowledge withdrawal',
    body: 'I confirm that {{document}} {{revision}} has been received and put into use on board.',
    bodyWithdrawal:
      'I confirm that the withdrawal of {{document}} {{revision}} has been received and printed copies have been removed.',
    password: 'Password',
    passwordHint: 'Enter your password to sign the acknowledgement.',
    confirm: 'Acknowledge',
    cancel: 'Cancel',
    done: 'Acknowledgement recorded.',
  },
  bell: {
    title: 'Documents',
    acknowledge_one: '{{count}} revision awaiting acknowledgement',
    acknowledge_other: '{{count}} revisions awaiting acknowledgement',
    acknowledge_withdrawal_one: '{{count}} withdrawal awaiting acknowledgement',
    acknowledge_withdrawal_other: '{{count}} withdrawals awaiting acknowledgement',
    download_problems_one: '{{count}} file could not be downloaded',
    download_problems_other: '{{count}} files could not be downloaded',
    downloading_one: '{{count}} new revision downloading',
    downloading_other: '{{count}} new revisions downloading',
    upcoming_one: '{{count}} revision takes effect soon',
    upcoming_other: '{{count}} revisions take effect soon',
    withdrawn_no_current_one: '{{count}} document withdrawn – remove printed copies',
    withdrawn_no_current_other: '{{count}} documents withdrawn – remove printed copies',
    read_one: '{{count}} document to read',
    read_other: '{{count}} documents to read',
    disk: {
      low: 'Disk space is low on the ship server',
      critical: 'Disk space is critical on the ship server',
    },
    quota: 'Document storage at {{percent}}% of the quota',
  },
  errors: {
    GENERIC: 'The operation failed. Try again.',
    NETWORK: 'No connection. Check the network and try again.',
    FORBIDDEN: 'You are not allowed to do this.',
    NOT_FOUND: 'The document was not found. It may have been removed.',
    INVALID_PASSWORD: 'The password is wrong.',
    INVALID_CREDENTIALS: 'The password is wrong.',
    TOO_MANY_ATTEMPTS: 'Too many attempts. Wait 15 minutes and try again.',
    FILE_NOT_ON_BOARD: 'The file is not on board yet.',
    FILE_NOT_VERIFIED: 'The file is not verified on board yet.',
    REVISION_NOT_CURRENT: 'Only the current revision can be downloaded or printed on board.',
    PREVIEW_NOT_READY: 'The preview is still loading. Try again in a moment.',
    PREVIEW_TOO_LARGE: 'The file expands too much to preview here. Download it to open.',
    PREVIEW_UNAVAILABLE:
      'This file cannot be printed here. Download it and print it from its own application.',
    CONTENT_CHANGED: 'The revision changed. Reopen it and try again.',
    FILES_UNAVAILABLE: 'The ship server has no file storage.',
    VALIDATION_FAILED: 'Some fields are invalid.',
    ROW_VERSION_CONFLICT: 'Someone else changed this record. Reopen it and try again.',
    CANCELLED: 'Cancelled.',
    ATTACHMENT_LOCKED:
      'An attachment already added to the draft revision cannot be removed. Discard the draft to start again.',
    // Onay akışı ve yönetim (Task 17)
    SELF_APPROVAL: 'You cannot approve a revision you uploaded or submitted.',
    NOT_APPROVER: 'Only the approver named in the approval rule can decide.',
    NOT_APPROVED: 'Only an approved revision can be published.',
    NOT_SUBMITTED: 'The revision has not been submitted for approval.',
    APPROVAL_RULE_MISSING:
      'There is no approval rule for your role. Ask the administrator to add one to the approval matrix.',
    CONTENT_HASH_MISMATCH:
      'The revision changed while you were viewing it. Reopen it and try again.',
    OPEN_REVISION_EXISTS: 'This document already has an open revision. Finish or discard it first.',
    REASON_REQUIRED: 'Enter a reason.',
    INVALID_TRANSITION: 'This action is not possible in the current state.',
    REAUTHENTICATION_REQUIRED: 'Enter your password.',
    EFFECTIVE_DATE_IN_PAST: 'The effective date cannot be in the past.',
    EFFECTIVE_DATE_TOO_FAR: 'The effective date is too far in the future.',
    DUPLICATE_DOC_NO: 'Another document in this category has this number.',
    DUPLICATE_FOLDER_CODE: 'Another category at this level has this code.',
    DUPLICATE_REVISION_NO: 'This revision number is already used.',
    FOLDER_NOT_EMPTY: 'Only empty categories can be deleted.',
    FOLDER_CYCLE: 'A category cannot be moved into itself.',
    // Ağaç işlemleri (ADR 0038 §8, §9)
    FOLDER_RETIRED: 'This folder is retired and read-only. Restore it first.',
    FOLDER_PARENT_RETIRED: 'The parent folder is retired. Restore the parent first.',
    DOCUMENT_IN_USE:
      'This document was used (form records, acknowledgements or read confirmations) and cannot be removed. Correct it with a new revision.',
    FOLDER_DELETE_FORBIDDEN:
      'This folder cannot be removed: only an empty folder added by the office, while the vessel is in setup. Retire it instead.',
    FOLDER_HAS_LIVE_CHILDREN: 'Retire the live subfolders first.',
    FOLDER_SECTION_MISMATCH: 'A folder can only move within its own section.',
    DUPLICATE_KEY: 'This folder already exists on the vessel.',
    TREE_OPERATION_REJECTED:
      'The change was rejected on at least one vessel. No vessel was changed.',
    OFFICE_IS_WRITER: 'The folder tree is managed by the office.',
    FOLDERS_ALREADY_EXIST: 'This section already has categories.',
    KIND_NOT_ALLOWED_IN_SECTION: 'This type is not allowed in this section.',
    FILE_TOO_LARGE: 'The file is larger than the limit.',
    UNSUPPORTED_FILE_TYPE: 'This file type is not supported.',
    FILE_TYPE_MISMATCH: 'The file content does not match its extension.',
    EMPTY_FILE: 'The file is empty.',
    INVALID_FILE_NAME: 'The file name is not valid.',
    UPLOAD_FAILED: 'The upload failed. Try again.',
    // Doküman ekranlarının alabildiği öteki kodlar (inceleme M8a3-R31)
    UNAUTHENTICATED: 'Sign in to continue.',
    SESSION_EXPIRED: 'Your session has expired. Sign in again.',
    SHIP_UNREACHABLE: 'The ship server cannot be reached. Check the ship network and try again.',
    SERVER_CERT_CHANGED:
      'The ship server’s certificate has changed. Ask the administrator to pair this PC again.',
    DOCUMENT_NOT_VISIBLE: 'This document is not distributed to this vessel.',
    ALREADY_ACKNOWLEDGED: 'This revision has already been acknowledged on board.',
    ALREADY_CONFIRMED: 'You have already confirmed reading this revision.',
    READ_NOT_REQUIRED: 'No read confirmation is expected from you for this revision.',
    QUOTA_EXCEEDED: 'The vessel’s document storage quota is full.',
    DISK_FULL: 'The ship server’s disk is full.',
    UPLOAD_CHANGED: 'The uploaded file changed. Upload it again.',
    UPLOAD_PURPOSE_MISMATCH: 'This upload cannot be used here. Upload the file again.',
    UNSUPPORTED: 'This browser cannot write to a folder. Use Chrome or Edge.',
    // Onay zinciri (ADR 0071 §2)
    INVALID_APPROVAL_CHAIN: 'The approval chain is not valid.',
    CHAIN_ROLE_REPEATED: 'A rank can appear only once in the chain.',
    UNKNOWN_ROLE: 'A selected role no longer exists.',
    FORM_OFFICE_STEP_NOT_ALLOWED: 'ISM forms do not go to the office for approval.',
    SCOPE_CHANGE_UNCONFIRMED: 'Confirm that the distribution changes now.',
    COMPANY_DOCUMENT_READ_ONLY: 'Company documents are read only on board.',
    // ISM formunu yalnız şirket yayınlar (sahip kararı 2026-10-17; ADR 0073); eski sunucu
    VESSEL_FORMS_COMPANY_ONLY:
      'ISM forms are published by the company. The vessel cannot add or submit its own form.',
    // Gemide doküman yazılmaz (sahip kararı 2026-10-18; ADR 0081)
    DOCUMENTS_COMPANY_ONLY:
      'Only the company adds, revises and publishes documents. The vessel reads them and fills in forms.',
    SHIP_FILE_MISSING: 'The file is not on the ship server. Choose it again.',
    HASH_MISMATCH: 'The file changed while it was checked. Choose it again.',
    FILE_NOT_UPLOADED: 'The vessel has not uploaded this file yet.',
    READ_ONLY: 'This library is read only.',
    PUBLISHED_REVISION_EXISTS: 'A document with a published revision cannot be deleted.',
    DISK_CRITICAL: 'The ship server’s disk is almost full. Free space before uploading.',
    SHIP_IS_WRITER: 'Only the vessel can change its own documents.',
    IMPORT_RUNNING: 'Another upload is running. Wait for it to finish.',
  },
  library: documentsLibraryEn,
  ...documentsOfficeEn,
};

export const documentsTr: typeof documentsEn = {
  sections: {
    ism: {
      title: 'ISM Formları',
      tree: 'Form Ağacı',
      details: 'Form Ayrıntıları',
      information: 'Form Bilgisi',
      search: 'Form ara...',
      view: 'Form Görünümü',
      number: 'Form No',
      name: 'Form Adı',
      open: 'Formu Aç',
      download: 'Formu İndir',
      print: 'Formu Yazdır',
      empty: 'Kütüphanede henüz form yok.',
    },
    manuals: {
      title: 'El Kitapları',
      tree: 'El Kitabı Ağacı',
      details: 'El Kitabı Ayrıntıları',
      information: 'El Kitabı Bilgisi',
      search: 'El kitabı ara...',
      view: 'Doküman Görünümü',
      number: 'Doküman No',
      name: 'Doküman Adı',
      open: 'Dokümanı Aç',
      download: 'Dokümanı İndir',
      print: 'Dokümanı Yazdır',
      empty: 'Kütüphanede henüz el kitabı yok.',
    },
    pms_instructions: {
      title: 'PMS Talimatları',
      tree: 'Talimat Ağacı',
      details: 'Talimat Ayrıntıları',
      information: 'Talimat Bilgisi',
      search: 'Talimat ara...',
      view: 'Talimat Görünümü',
      number: 'Talimat No',
      name: 'Talimat Adı',
      open: 'Talimatı Aç',
      download: 'Talimatı İndir',
      print: 'Talimatı Yazdır',
      empty: 'Kütüphanede henüz PMS talimatı yok.',
    },
  },
  remove: {
    title: '{{title}} dokümanını kaldır',
    text: 'Yanlışlıkla yüklenen doküman için kullanın. Doküman kütüphaneden çıkar; revizyonları ve geçmişi kalır. Yürürlükteki doküman önce geri çekilir; kullanılmış doküman (form kaydı, teyit, okuma beyanı) kaldırılamaz, yeni revizyonla düzeltilir.',
    cancel: 'Vazgeç',
    confirm: 'Dokümanı kaldır',
    done: 'Doküman kaldırıldı.',
  },
  head: {
    edit: 'Yeni revizyon',
    remove: 'Dokümanı kaldır',
    download: 'İndir',
    print: 'Yazdır',
    distribution: 'Dağıtım',
    newDocument: 'Yeni Doküman',
    newDocumentFormHint:
      'Yeni Doküman kütüphaneye yeni form ekler. Bir formu doldurmak için formu seçip Formu doldur’u kullanın.',
    editBlocked: '{{revision}} henüz açık. Yeni revizyondan önce onu tamamlayın ya da vazgeçin.',
    revisionHint:
      'Yeni revizyonu Ofis Yöneticisi ya da Şirket Yöneticisi hazırlar; siz onaylar ve gemilere yayınlarsınız.',
  },
  fields: {
    category: 'Kategori',
    fileType: 'Dosya Türü',
    version: 'Sürüm',
    updated: 'Güncellendi',
    lastUpdated: 'Son Güncelleme',
    createdBy: 'Hazırlayan',
    description: 'Açıklama',
    noDescription: 'Açıklama yok.',
  },
  tabs: {
    information: 'Bilgi',
    history: 'Geçmiş',
    attachments: 'Ekler',
  },
  actions: {
    openDocument: 'Dokümanı Aç',
    title: 'İşlemler',
    openHint: 'Rolünüzle bu formun kaydını açamazsınız.',
    officeRecords:
      'Bu formun kayıtları gemide, gemi uygulamasında girilir: formu orada açın, doldurun, dosyayı yükleyin ve gönderin. Gönderilen kayıtlar ve onaylar Form Geçmişi’nde görünür.',
    officeHint: 'Kayıtlar ofiste değil, gemide girilir.',
    formHistory: 'Form Geçmişi’ni aç',
    favoriteAdd: 'Favorilere Ekle',
    favoriteRemove: 'Favorilerden Çıkar',
  },
  related: {
    title: 'İlgili Dokümanlar',
    none: 'İlgili doküman yok.',
    download: '{{name}} indir',
    notAvailable: 'Yürürlükte revizyon yok',
  },
  fileKinds: {
    docx: 'Word',
    xlsx: 'Excel',
    pdf: 'PDF',
    jpg: 'Resim',
    png: 'Resim',
  },
  fileKindsLong: {
    docx: 'Word Belgesi',
    xlsx: 'Excel Çalışma Kitabı',
    pdf: 'PDF Belgesi',
    jpg: 'JPEG Resmi',
    png: 'PNG Resmi',
  },
  states: {
    draft: 'Taslak',
    pending_approval: 'Onay Bekliyor',
    approved: 'Onaylandı',
    scheduled: 'Zamanlandı',
    published: 'Yayında',
    superseded: 'Yürürlükten Kalktı',
    withdrawn: 'Geri Çekildi',
    cancelled: 'İptal Edildi',
    discarded: 'Vazgeçildi',
  },
  badges: {
    updated: 'Güncellendi',
    withdrawn: 'Geri Çekildi',
    notOnBoard: 'Gemide değil',
    favorite: 'Favori',
  },
  info: {
    number: 'Numara',
    title: 'Başlık',
    kind: 'Tür',
    kinds: { form: 'Form', procedure: 'Prosedür', manual: 'El Kitabı' },
    folder: 'Klasör',
    scope: 'Dağıtım',
    scopes: { fleet: 'Bütün gemiler', vessels: 'Seçili gemiler', office: 'Yalnız ofis' },
    branch: 'Dal',
    main: 'Ana',
    currentRevision: 'Yürürlükteki revizyon',
    effective: 'Yürürlük tarihi',
    preparedBy: 'Hazırlayan',
    approvedBy: 'Onaylayan',
    changeSummary: 'Değişiklik özeti',
    noCurrent: 'Yürürlükte revizyon yok',
    scheduled: 'Rev. {{revision}} {{date}} tarihinde yürürlüğe girer.',
  },
  history: {
    seq: '#',
    revision: 'Rev. No',
    status: 'Durum',
    date: 'Tarih',
    uploadedBy: 'Yükleyen',
    approvedBy: 'Onaylayan',
    summary: 'Değişiklik özeti',
    preview: 'Rev. {{revision}} önizle',
    previewing: 'Gösteriliyor',
    empty: 'Henüz revizyon yok.',
    caption: 'Ana dalın revizyonları',
  },
  attachments: {
    file: 'Dosya',
    role: 'Tür',
    roles: { attachment: 'Ek', print_rendition: 'Baskı PDF’i' },
    size: 'Boyut',
    empty: 'Bu revizyonun eki yok.',
  },
  tree: {
    empty: 'Bu bölümde henüz doküman yok.',
    noMatch: '“{{query}}” ile eşleşen doküman yok.',
    clear: 'Aramayı temizle',
    count_one: '{{count}} doküman',
    count_other: '{{count}} doküman',
    retiredGroup: 'Emekli klasörler',
    otherGroup: 'Diğer şirket dokümanları',
    company: 'Şirket',
    awaitingApproval: 'onay bekliyor',
    openRevision: {
      draft: '{{revision}} taslak',
      pending_approval: '{{revision}} onay bekliyor',
      approved: '{{revision}} onaylandı',
      scheduled: '{{revision}} zamanlandı',
    },
    sort: {
      label: 'Dokümanları sırala',
      order: 'Sıra',
      date: 'Tarih',
    },
    // Formun altındaki kayıtlar (ADR 0072)
    records: documentsTreeRecordsTr,
  },
  screen: {
    loading: 'Kütüphane yükleniyor…',
    error: 'Kütüphane okunamadı.',
    retry: 'Yeniden dene',
    select: 'Ağaçtan bir doküman seçin.',
    noRevision: 'Bu dokümanın henüz revizyonu yok.',
    detailError: 'Doküman okunamadı.',
    showTree: 'Ağacı göster',
    dismiss: 'Kapat',
    officeEmpty:
      'Yeni Doküman ile doküman ekleyin. Revize etmek için dokümanı seçip Yeni revizyon’u kullanın; DPA onayladıktan sonra Gemilere yayınla, dokümanı dağıtımındaki gemilere gönderir.',
  },
  viewer: {
    toolbar: 'Önizleme',
    zoomOut: 'Uzaklaştır',
    zoomIn: 'Yakınlaştır',
    fit: 'Genişliğe sığdır',
    zoom: 'Yakınlaştırma',
    download: 'İndir',
    print: 'Yazdır',
    fullscreen: 'Tam ekran',
    exitFullscreen: 'Tam ekrandan çık',
    page: 'Sayfa {{page}} / {{count}}',
    pageApprox: 'Sayfa {{page}} / ≈{{count}}',
    approximate: 'Yaklaşık önizleme: sayfa kırılımları Word’den farklı olabilir.',
    loading: 'Önizleme yükleniyor…',
    error: 'Dosya önizlenemedi.',
    unsupported: 'Bu dosya türü önizlenemez.',
    tooLarge: 'Dosya burada önizlemek için çok büyük ({{size}}). Açmak için indirin.',
    sheets: 'Sayfalar',
    notCurrent: 'Bu revizyon yürürlükte değil – yalnız başvuru içindir. Yine de indirilsin mi?',
    notCurrentShip: 'Gemide yalnız yürürlükteki revizyon indirilir ve yazdırılır.',
    blocked: {
      noRevision: 'Bu dokümanın henüz revizyonu yok.',
      notCurrent: 'Gemide yalnız yürürlükteki revizyon indirilir ve yazdırılır.',
      notOnBoard: 'Dosya henüz gemide değil.',
    },
    stateNotice: 'Bu revizyon yürürlükte değil ({{state}}): yalnız başvuru içindir.',
    preparingPrint: 'Sayfalar yazdırmaya hazırlanıyor…',
    find: 'Dokümanda bul',
    findNext: 'Sonraki eşleşme',
    findPrevious: 'Önceki eşleşme',
    findClose: 'Aramayı kapat',
    matches: '{{index}} / {{count}}',
    noMatches: 'Eşleşme yok',
    truncated:
      '{{totalRows}} × {{totalColumns}} içinden ilk {{rows}} satır ve {{columns}} sütun gösteriliyor. Tamamı için dosyayı indirin.',
  },
  printFooter,
  notOnBoard: {
    title: 'Henüz gemide değil',
    description:
      'Bu revizyonun dosyası gemi sunucusuna henüz inmedi. Gemide doğrulanınca önizlenebilir.',
    position: 'Kuyruk sırası',
    size: 'Boyut',
    reason: 'Durum',
    reasons: {
      pending: 'İndirilmeyi bekliyor',
      downloading: 'İniyor',
      deferred_quota: 'Ertelendi: depolama kotası ya da disk alanı',
      failed: 'İndirme başarısız',
      window: 'Toplu indirme penceresini bekliyor',
      paused: 'Duraklatıldı: gemide toplu indirme duraklatıldı',
      manual: 'Elle indirme bekliyor (Şimdi indir)',
      monthly_cap: 'Bekliyor: aylık toplu indirme sınırı doldu',
      retry: 'Bağlantı sorunundan sonra yeniden denenecek',
      unknown: 'İndirme kuyruğunda değil',
    },
    downloadNow: 'Şimdi indir',
    requested: 'İndirme istendi. Bir sonraki bağlantıda başlar.',
  },
  changes: {
    title: '{{revision}} ile ne değişti',
    acknowledge: 'Teyit et',
    acknowledgeWithdrawal: 'Geri çekmeyi teyit et',
    acknowledged: '{{name}} {{date}} tarihinde teyit etti',
    withdrawn: '{{revision}} geri çekildi. Basılı kopyaları kaldırın.',
    upcoming: '{{revision}} {{date}} tarihinde yürürlüğe girer. Basılı kopyaları o gün değiştirin.',
  },
  acknowledge: {
    title: 'Revizyonu teyit et',
    titleWithdrawal: 'Geri çekmeyi teyit et',
    // İmzalanan beyan birinci şahıs: EN "I confirm that…" ile aynı anlam (inceleme M8a3-R30)
    body: '{{document}} {{revision}} revizyonunun gemide alındığını ve uygulamaya alındığını teyit ederim.',
    bodyWithdrawal:
      '{{document}} {{revision}} geri çekmesinin gemide alındığını ve basılı kopyaların kaldırıldığını teyit ederim.',
    password: 'Parola',
    passwordHint: 'Teyidi imzalamak için parolanızı girin.',
    confirm: 'Teyit et',
    cancel: 'Vazgeç',
    done: 'Teyit kaydedildi.',
  },
  bell: {
    title: 'Dokümanlar',
    acknowledge_one: '{{count}} revizyon teyit bekliyor',
    acknowledge_other: '{{count}} revizyon teyit bekliyor',
    acknowledge_withdrawal_one: '{{count}} geri çekme teyit bekliyor',
    acknowledge_withdrawal_other: '{{count}} geri çekme teyit bekliyor',
    download_problems_one: '{{count}} dosya indirilemedi',
    download_problems_other: '{{count}} dosya indirilemedi',
    downloading_one: '{{count}} yeni revizyon iniyor',
    downloading_other: '{{count}} yeni revizyon iniyor',
    upcoming_one: '{{count}} revizyon yakında yürürlüğe giriyor',
    upcoming_other: '{{count}} revizyon yakında yürürlüğe giriyor',
    withdrawn_no_current_one: '{{count}} doküman geri çekildi – basılı kopyaları kaldırın',
    withdrawn_no_current_other: '{{count}} doküman geri çekildi – basılı kopyaları kaldırın',
    read_one: 'Okunacak {{count}} doküman',
    read_other: 'Okunacak {{count}} doküman',
    disk: {
      low: 'Gemi sunucusunda disk alanı azaldı',
      critical: 'Gemi sunucusunda disk alanı kritik',
    },
    quota: 'Doküman deposu kotanın %{{percent}} düzeyinde',
  },
  errors: {
    GENERIC: 'İşlem başarısız. Yeniden deneyin.',
    NETWORK: 'Bağlantı yok. Ağı denetleyip yeniden deneyin.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    NOT_FOUND: 'Doküman bulunamadı. Kaldırılmış olabilir.',
    INVALID_PASSWORD: 'Parola yanlış.',
    INVALID_CREDENTIALS: 'Parola yanlış.',
    TOO_MANY_ATTEMPTS: 'Çok fazla deneme. 15 dakika bekleyip yeniden deneyin.',
    FILE_NOT_ON_BOARD: 'Dosya henüz gemide değil.',
    FILE_NOT_VERIFIED: 'Dosya gemide henüz doğrulanmadı.',
    REVISION_NOT_CURRENT: 'Gemide yalnız yürürlükteki revizyon indirilir ve yazdırılır.',
    PREVIEW_NOT_READY: 'Önizleme henüz yükleniyor. Birazdan yeniden deneyin.',
    PREVIEW_TOO_LARGE: 'Dosya burada önizlemek için fazla büyük açılıyor. Açmak için indirin.',
    PREVIEW_UNAVAILABLE: 'Bu dosya burada basılamaz. İndirip kendi uygulamasından basın.',
    CONTENT_CHANGED: 'Revizyon değişti. Yeniden açıp deneyin.',
    FILES_UNAVAILABLE: 'Gemi sunucusunda dosya deposu yok.',
    VALIDATION_FAILED: 'Bazı alanlar geçersiz.',
    ROW_VERSION_CONFLICT: 'Kaydı başka biri değiştirdi. Yeniden açıp deneyin.',
    CANCELLED: 'İptal edildi.',
    ATTACHMENT_LOCKED:
      'Taslak revizyona eklenmiş ek kaldırılamaz. Baştan başlamak için taslaktan vazgeçin.',
    SELF_APPROVAL: 'Yüklediğiniz ya da onaya gönderdiğiniz revizyonu onaylayamazsınız.',
    NOT_APPROVER: 'Yalnız onay kuralındaki onaylayan karar verebilir.',
    NOT_APPROVED: 'Yalnız onaylanmış revizyon yayınlanabilir.',
    NOT_SUBMITTED: 'Revizyon onaya gönderilmedi.',
    APPROVAL_RULE_MISSING:
      'Rolünüz için onay kuralı yok. Yöneticiden onay matrisine kural eklemesini isteyin.',
    CONTENT_HASH_MISMATCH: 'Siz bakarken revizyon değişti. Yeniden açıp deneyin.',
    OPEN_REVISION_EXISTS: 'Bu dokümanın açık bir revizyonu var. Önce onu bitirin ya da vazgeçin.',
    REASON_REQUIRED: 'Gerekçe girin.',
    INVALID_TRANSITION: 'Bu işlem şimdiki durumda yapılamaz.',
    REAUTHENTICATION_REQUIRED: 'Parolanızı girin.',
    EFFECTIVE_DATE_IN_PAST: 'Yürürlük tarihi geçmişte olamaz.',
    EFFECTIVE_DATE_TOO_FAR: 'Yürürlük tarihi çok ileride.',
    DUPLICATE_DOC_NO: 'Bu kategoride aynı numaralı başka doküman var.',
    DUPLICATE_FOLDER_CODE: 'Bu düzeyde aynı kodlu başka kategori var.',
    DUPLICATE_REVISION_NO: 'Bu revizyon numarası kullanılmış.',
    FOLDER_NOT_EMPTY: 'Yalnız boş kategori silinir.',
    FOLDER_CYCLE: 'Kategori kendi içine taşınamaz.',
    FOLDER_RETIRED: 'Bu klasör emekli ve salt okunur. Önce geri alın.',
    FOLDER_PARENT_RETIRED: 'Üst klasör emekli. Önce üst klasörü geri alın.',
    DOCUMENT_IN_USE:
      'Bu doküman kullanıldı (form kaydı, teyit ya da okuma beyanı); kaldırılamaz. Yeni revizyonla düzeltin.',
    FOLDER_DELETE_FORBIDDEN:
      'Bu klasör kaldırılamaz: yalnız gemi kurulumdayken ofisin açtığı boş klasör kaldırılır. Emekliye ayırın.',
    FOLDER_HAS_LIVE_CHILDREN: 'Önce canlı alt klasörleri emekliye ayırın.',
    FOLDER_SECTION_MISMATCH: 'Klasör yalnız kendi bölümünde taşınabilir.',
    DUPLICATE_KEY: 'Bu klasör gemide zaten var.',
    TREE_OPERATION_REJECTED: 'Değişiklik en az bir gemide reddedildi. Hiçbir gemi değişmedi.',
    OFFICE_IS_WRITER: 'Klasör ağacını ofis yönetir.',
    FOLDERS_ALREADY_EXIST: 'Bu bölümde zaten kategori var.',
    KIND_NOT_ALLOWED_IN_SECTION: 'Bu tür bu bölümde kullanılamaz.',
    FILE_TOO_LARGE: 'Dosya sınırdan büyük.',
    UNSUPPORTED_FILE_TYPE: 'Bu dosya türü desteklenmiyor.',
    FILE_TYPE_MISMATCH: 'Dosyanın içeriği uzantısıyla uyuşmuyor.',
    EMPTY_FILE: 'Dosya boş.',
    INVALID_FILE_NAME: 'Dosya adı geçersiz.',
    UPLOAD_FAILED: 'Yükleme başarısız. Yeniden deneyin.',
    // Doküman ekranlarının alabildiği öteki kodlar (inceleme M8a3-R31)
    UNAUTHENTICATED: 'Devam etmek için giriş yapın.',
    SESSION_EXPIRED: 'Oturumunuz sona erdi. Yeniden giriş yapın.',
    SHIP_UNREACHABLE: 'Gemi sunucusuna ulaşılamıyor. Gemi ağını denetleyip yeniden deneyin.',
    SERVER_CERT_CHANGED:
      'Gemi sunucusunun sertifikası değişti. Bu bilgisayarı yeniden eşleştirmesi için yöneticiye başvurun.',
    DOCUMENT_NOT_VISIBLE: 'Bu doküman bu gemiye dağıtılmıyor.',
    ALREADY_ACKNOWLEDGED: 'Bu revizyon gemide zaten teyit edildi.',
    ALREADY_CONFIRMED: 'Bu revizyonu okuduğunuzu zaten beyan ettiniz.',
    READ_NOT_REQUIRED: 'Bu revizyon için sizden okuma beyanı beklenmiyor.',
    QUOTA_EXCEEDED: 'Geminin doküman depolama kotası dolu.',
    DISK_FULL: 'Gemi sunucusunun diski dolu.',
    UPLOAD_CHANGED: 'Yüklenen dosya değişti. Yeniden yükleyin.',
    UPLOAD_PURPOSE_MISMATCH: 'Bu yükleme burada kullanılamaz. Dosyayı yeniden yükleyin.',
    UNSUPPORTED: 'Bu tarayıcı klasöre yazamaz. Chrome ya da Edge kullanın.',
    INVALID_APPROVAL_CHAIN: 'Onay zinciri geçersiz.',
    CHAIN_ROLE_REPEATED: 'Bir rütbe zincirde yalnız bir kez yer alır.',
    UNKNOWN_ROLE: 'Seçilen rollerden biri artık yok.',
    FORM_OFFICE_STEP_NOT_ALLOWED: 'ISM formları ofis onayına gitmez.',
    SCOPE_CHANGE_UNCONFIRMED: 'Dağıtımın şimdi değişeceğini onaylayın.',
    COMPANY_DOCUMENT_READ_ONLY: 'Şirket dokümanları gemide salt okunurdur.',
    VESSEL_FORMS_COMPANY_ONLY:
      'ISM formlarını şirket yayınlar. Gemi kendi formunu ekleyemez ve gönderemez.',
    DOCUMENTS_COMPANY_ONLY:
      'Dokümanı yalnız şirket ekler, revize eder ve yayınlar. Gemi okur ve formları doldurur.',
    SHIP_FILE_MISSING: 'Dosya gemi sunucusunda yok. Yeniden seçin.',
    HASH_MISMATCH: 'Dosya denetlenirken değişti. Yeniden seçin.',
    FILE_NOT_UPLOADED: 'Gemi bu dosyayı henüz yüklemedi.',
    READ_ONLY: 'Bu kütüphane salt okunurdur.',
    PUBLISHED_REVISION_EXISTS: 'Yayınlanmış revizyonu olan doküman silinemez.',
    DISK_CRITICAL: 'Gemi sunucusunun diski dolmak üzere. Yüklemeden önce yer açın.',
    SHIP_IS_WRITER: 'Geminin kendi dokümanlarını yalnız gemi değiştirebilir.',
    IMPORT_RUNNING: 'Başka bir yükleme sürüyor. Bitmesini bekleyin.',
  },
  library: documentsLibraryTr,
  ...documentsOfficeTr,
};
